import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = 'https://starvoice3.github.io';
const formSubmitUrl = 'https://readdy.ai/api/form/da29amjk8r5te5mqsk4g';

const values = [
  { icon: 'ri-team-line', title: '完整', desc: '角色与声优信息尽量收录完整，方便观众一次查全。' },
  { icon: 'ri-verified-badge-line', title: '准确', desc: '内容以作品实际设定与官方信息为准，反复核对。' },
  { icon: 'ri-heart-3-line', title: '有温度', desc: '不只是资料，更想把角色带给我们的感动也一并整理。' },
  { icon: 'ri-eye-line', title: '易读', desc: '信息结构清晰，查一个人物或声优，三步之内搞定。' },
];

const voices = [
  { name: '阿岚', text: '以前只记得亚梦很酷，看完这里才知道她有那么多故事，补番指南太实用了。' },
  { name: '小樱', text: '声优表太全了，找水树奈奈配的歌呗直接点开就对上了，感动！' },
  { name: '柠檬茶', text: '角色背景整理得很细，几斗和歌呗那条线看得我又哭又笑的。' },
];

export default function About() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 反垃圾蜜罐：若被填充则不提交，并给出与正常提交一致的成功反馈
    const honeypot = data.get('company_alt')?.toString().trim() ?? '';
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === 'company_alt') return;
      payload.append(key, value.toString());
    });

    setStatus('submitting');
    setFormError('');
    try {
      const res = await fetch(formSubmitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let code = '';
      let serverMsg = '';
      try {
        const parsed = JSON.parse(responseText);
        code = parsed?.code ?? '';
        serverMsg =
          parsed?.meta?.message ??
          parsed?.message ??
          parsed?.meta?.detail ??
          '';
      } catch {
        /* ignore parse error */
      }

      if (res.ok && code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后再试。');
    }
  }

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于守护甜心档案',
      url: `${siteUrl}/about`,
      description: '守护甜心动漫角色大全与声优表资料站的介绍，包括网站定位、发展理念、内容原则与联系方式。',
      inLanguage: 'zh-CN',
      about: { '@type': 'Organization', name: '守护甜心档案' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '关于本站', item: `${siteUrl}/about` },
      ],
    },
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <JsonLd data={schema} />
      <nav aria-label="面包屑" className="mb-6">
        <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <li>
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
          </li>
          <li aria-hidden="true">
            <i className="ri-arrow-right-s-line" />
          </li>
          <li aria-current="page" className="font-medium text-foreground-800">
            关于本站
          </li>
        </ol>
      </nav>

      <div className="max-w-2xl">
        <h1 className="font-heading text-2xl font-black text-foreground-900 md:text-3xl">关于守护甜心档案</h1>
        <p className="mt-4 text-base leading-8 text-foreground-700">
          「守护甜心档案」是一个面向《守护甜心》动画爱好者的简体中文资料站。我们把日奈森亚梦、月咏几斗等主要角色的背景故事，以及守护甜心动漫声优表与配音演员信息，集中整理成一处，让每一位想查角色、对声优的观众，都能方便地找到答案。
        </p>
      </div>

      <section className="mt-10">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
          <i className="ri-compass-3-line text-primary-500" aria-hidden="true"></i>
          网站定位
        </h2>
        <p className="mt-3 max-w-3xl text-[15px] leading-8 text-foreground-700">
          我们相信，一部好的作品值得被认真记录。本站不做剧情搬运，而专注于「角色资料」与「配音阵容」两大检索场景：想了解某个角色的性格与故事走向，去角色大全；想确认某位声音背后的声优是谁，看声优表。简单、清晰、直达目标。
        </p>
      </section>

      <section className="mt-10">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
          <i className="ri-route-line text-primary-500" aria-hidden="true"></i>
          发展理念
        </h2>
        <p className="mt-3 max-w-3xl text-[15px] leading-8 text-foreground-700">
          从一部作品的粉丝出发，用作品本身打动我们的方式去打动更多人。我们愿意花时间核对设定、梳理角色关系，只为让资料经得起翻看。网站会随大家的反馈与需要持续补充与修正，让这份档案越用越好用。
        </p>
      </section>

      <section className="mt-10">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
          <i className="ri-clipboard-line text-primary-500" aria-hidden="true"></i>
          内容原则
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v) => (
            <article key={v.title} className="rounded-2xl border border-background-200/80 bg-background-50 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${v.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-900">{v.title}</h3>
              <p className="mt-1.5 text-sm leading-6 text-foreground-600">{v.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
          <i className="ri-chat-heart-line text-primary-500" aria-hidden="true"></i>
          用户口碑
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {voices.map((v) => (
            <figure key={v.name} className="rounded-2xl bg-background-100/70 p-5">
              <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true"></i>
              <blockquote className="mt-2 text-sm leading-7 text-foreground-700">{v.text}</blockquote>
              <figcaption className="mt-3 flex items-center gap-2 text-sm font-medium text-foreground-800">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-user-smile-line" aria-hidden="true"></i>
                </span>
                {v.name}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
            <i className="ri-mail-send-line text-primary-500" aria-hidden="true"></i>
            联系我们
          </h2>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            有想补充的角色资料、发现信息有误，或对网站有任何建议，欢迎填写下面的表单告诉我们。你的每一条反馈，都会让这份档案更完整。
          </p>
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-3 rounded-2xl border border-background-200/80 bg-background-50 p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                <i className="ri-mail-line text-xl" aria-hidden="true"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">意见反馈邮箱</p>
                <p className="text-sm font-medium text-foreground-800">hello@shugochara-fans.example.com</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-background-200/80 bg-background-50 p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary-100 text-secondary-700">
                <i className="ri-time-line text-xl" aria-hidden="true"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">回复时间</p>
                <p className="text-sm font-medium text-foreground-800">工作日 48 小时内回复</p>
              </div>
            </div>
          </div>
        </div>

        <form
          data-readdy-form="关于本站联系表单"
          id="about-contact-form"
          onSubmit={handleSubmit}
          className="rounded-2xl border border-background-200/80 bg-background-50 p-6 md:p-7"
        >
          <h3 className="font-heading text-lg font-bold text-foreground-900">给我们留言</h3>
          <div className="mt-5 space-y-4">
            <div>
              <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                称呼
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                placeholder="你的昵称"
                className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                邮箱
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                placeholder="用于接收回复"
                className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
                留言内容
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="想补充的资料、发现的错误或任何建议……"
                className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
              ></textarea>
              <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
            </div>
            {/* 反垃圾蜜罐字段 */}
            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="hp-field"
            />
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === 'submitting' ? (
                <>
                  <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                  提交中…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line" aria-hidden="true"></i>
                  提交留言
                </>
              )}
            </button>
          </div>

          {status === 'success' && (
            <p className="mt-4 rounded-lg bg-secondary-100 px-4 py-3 text-sm text-secondary-900" role="status">
              <i className="ri-checkbox-circle-line mr-1" aria-hidden="true"></i>
              已收到你的留言，感谢你的反馈！
            </p>
          )}
          {status === 'error' && (
            <p className="mt-4 rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-900" role="alert">
              <i className="ri-error-warning-line mr-1" aria-hidden="true"></i>
              {formError}
            </p>
          )}
        </form>
      </section>
    </div>
  );
}